import { useMemo, useState } from 'react';
import { useCreateMaintenanceWindow, useUpdateMaintenanceWindow } from '../../hooks/useMaintenance';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input, Select, Textarea, checkboxClass } from '../common/Input';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { parseServerDate } from '../../utils/datetime';
import { MaintenanceWindow, MaintenanceWindowInput, NotificationTarget } from '../../types';
import { TargetPicker } from '../notifications/TargetPicker';

const WEEKDAYS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 0, label: 'Sun' },
];

const pad = (n: number) => String(n).padStart(2, '0');

/** Server UTC timestamp -> `datetime-local` value in the browser's zone. */
function toLocalInput(value: string | null): string {
  const d = parseServerDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` value (browser zone) -> ISO 8601 UTC for the API. */
function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function browserTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

function timezoneList(): string[] {
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
    const list = intl.supportedValuesOf?.('timeZone');
    if (list && list.length) return list;
  } catch { /* fall through */ }
  return ['UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'America/New_York', 'America/Chicago',
    'America/Denver', 'America/Los_Angeles', 'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney', 'Australia/Melbourne'];
}

/** Round "now" up to the next quarter hour so the default one-off window starts in the future. */
function defaultStart(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil((d.getMinutes() + 1) / 15) * 15);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function plusHours(local: string, hours: number): string {
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '';
  d.setHours(d.getHours() + hours);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Props {
  window?: MaintenanceWindow;
  onClose: () => void;
}

export function MaintenanceForm({ window: existing, onClose }: Props) {
  const isEdit = Boolean(existing);
  const create = useCreateMaintenanceWindow();
  const update = useUpdateMaintenanceWindow();
  const { addToast } = useToast();

  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [kind, setKind] = useState<'once' | 'recurring'>(existing?.recurrence ? 'recurring' : 'once');
  const [startAt, setStartAt] = useState(() => existing?.start_at ? toLocalInput(existing.start_at) : defaultStart());
  const [endAt, setEndAt] = useState(() => existing?.end_at ? toLocalInput(existing.end_at) : plusHours(defaultStart(), 1));
  const [weekdays, setWeekdays] = useState<number[]>(existing?.recurrence?.weekdays ?? [0]);
  const [startTime, setStartTime] = useState(existing?.recurrence?.start_time ?? '02:00');
  const [endTime, setEndTime] = useState(existing?.recurrence?.end_time ?? '04:00');
  const [timezone, setTimezone] = useState(existing?.timezone ?? browserTimezone());
  const [targets, setTargets] = useState<NotificationTarget[]>(
    (existing?.targets ?? []).filter(t => t.target_type !== 'all'),
  );
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [error, setError] = useState('');

  const timezones = useMemo(timezoneList, []);
  const crossesMidnight = kind === 'recurring' && endTime < startTime;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const data: MaintenanceWindowInput = {
      name,
      description: description || null,
      enabled,
      targets,
    };

    if (kind === 'once') {
      const start = fromLocalInput(startAt);
      const end = fromLocalInput(endAt);
      if (!start || !end) { setError('Give both a start and an end.'); return; }
      if (end <= start) { setError('The end must be after the start.'); return; }
      data.start_at = start;
      data.end_at = end;
      data.recurrence = null;
    } else {
      if (weekdays.length === 0) { setError('Pick at least one weekday.'); return; }
      if (startTime === endTime) { setError('Start and end times must differ.'); return; }
      data.recurrence = { weekdays: [...weekdays].sort(), start_time: startTime, end_time: endTime };
      data.timezone = timezone;
      data.start_at = null;
      data.end_at = null;
    }

    if (isEdit && existing) {
      update.mutate({ id: existing.id, data }, {
        onSuccess: () => { addToast('success', 'Maintenance window updated'); onClose(); },
        onError: err => addToast('error', apiError(err, 'Failed to update maintenance window')),
      });
    } else {
      create.mutate(data, {
        onSuccess: () => { addToast('success', 'Maintenance window created'); onClose(); },
        onError: err => addToast('error', apiError(err, 'Failed to create maintenance window')),
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Field label="Name" required>
        {p => <Input {...p} type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Weekly database backup" />}
      </Field>

      <Field label="Description (optional)">
        {p => <Textarea {...p} rows={2} value={description} onChange={e => setDescription(e.target.value)} />}
      </Field>

      <fieldset>
        <legend className="block text-sm font-medium mb-1">Schedule</legend>
        <div className="flex gap-4">
          <label className="inline-flex items-center gap-1.5 text-sm">
            <input type="radio" name="kind" className={checkboxClass} checked={kind === 'once'} onChange={() => setKind('once')} />
            One-off
          </label>
          <label className="inline-flex items-center gap-1.5 text-sm">
            <input type="radio" name="kind" className={checkboxClass} checked={kind === 'recurring'} onChange={() => setKind('recurring')} />
            Recurring
          </label>
        </div>
      </fieldset>

      {kind === 'once' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Starts" required hint={`Your local time (${browserTimezone()}).`}>
            {p => (
              <Input
                {...p}
                type="datetime-local"
                required
                value={startAt}
                onChange={e => {
                  setStartAt(e.target.value);
                  if (!endAt || endAt <= e.target.value) setEndAt(plusHours(e.target.value, 1));
                }}
              />
            )}
          </Field>
          <Field label="Ends" required>
            {p => <Input {...p} type="datetime-local" required min={startAt} value={endAt} onChange={e => setEndAt(e.target.value)} />}
          </Field>
        </div>
      ) : (
        <div className="space-y-4">
          <fieldset>
            <legend className="block text-sm font-medium mb-1">On</legend>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(d => {
                const on = weekdays.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setWeekdays(on ? weekdays.filter(w => w !== d.value) : [...weekdays, d.value])}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                      on
                        ? 'bg-brand-600 border-brand-600 text-white'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="From" required>
              {p => <Input {...p} type="time" required value={startTime} onChange={e => setStartTime(e.target.value)} />}
            </Field>
            <Field label="Until" required hint={crossesMidnight ? 'Ends the following day.' : undefined}>
              {p => <Input {...p} type="time" required value={endTime} onChange={e => setEndTime(e.target.value)} />}
            </Field>
            <Field label="Timezone">
              {p => (
                <Select {...p} value={timezone} onChange={e => setTimezone(e.target.value)}>
                  {!timezones.includes(timezone) && <option value={timezone}>{timezone}</option>}
                  {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </Select>
              )}
            </Field>
          </div>
        </div>
      )}

      <TargetPicker value={targets} onChange={setTargets} allLabel="All services" someLabel="Only these" />

      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" className={checkboxClass} checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        Enabled
      </label>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        While the window is active the targeted services show <strong className="font-medium">Maintenance</strong>,
        no down/degraded/recovery notifications are sent for them, and the time is left out of their uptime.
      </p>

      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="flex gap-3 pt-1">
        <Button type="submit" loading={create.isPending || update.isPending}>
          {isEdit ? 'Save changes' : 'Create window'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}
